﻿import { DeleteRequest, deleteSingle, EditResponse, getTreeData, SetRequest, setSingle, TreeDataNode, TreeDataNodeValidKeys, TreeDataResponse } from "../../API/AutoNotifications/AutoNotificationThresholds";
import { LiveDataSet } from "../../API/LiveData/LiveDataSet";
import { RESTError } from "../../API/RESTError";
import { IHashData } from "../../IHashData";
import { IPage } from "../../IPage";
import { MainSite } from "../../MainSite";
import { AutoNotificationThreshold } from "../../Model/AutoNotificationThreshold";
import { IRegion } from "../../Model/Region";

export class AutoNotificationThresholdsPage implements IPage {
    public id: string = 'autoNotificationThresholds';

    constructor(private mainSite: MainSite) {
    }

    public initialize(): void {
    }

    protected hasLiveData: boolean = false;

    private container: JQuery = null;
    private pageHeaderContainer: JQuery = null;
    private loadingContainer: JQuery = null;
    private table: JQuery = null;
    private tableBody: JQuery = null;
    private tableHead: JQuery = null;
    private tableHeadRow: JQuery = null;
    private modal: JQuery = null;

    private tree: Fancytree.Fancytree = null;
    private $treeCont: JQuery = null;
    private filterInput: JQuery = null;
    private filterItemsSetCheckbox: JQuery = null;
    private checkboxEnableRowClick: JQuery = null;
    private isEditingEntry: boolean = false; //: NB: implemented as FancyTree seems to handle some double-clicking as single click (special "fancytreeclick") events. So to enable editing an entry by clicking anywhere on a row (except expanders for folders), we use this to prevent editor being opened multiple times.

    protected modalBodyContent: JQuery;
    protected modalFieldStationId: JQuery;
    protected modalFieldParameterGroup: JQuery;
    protected modalFieldSensor: JQuery;
    protected modalFieldMasked: JQuery;
    protected modalSaveButton: JQuery;
    protected modalDeleteButton: JQuery;

    protected data: TreeDataResponse;


    public handleHashChange(hashData: IHashData): boolean {
        if (hashData.Page === this.id) {
            if (!this.container) {
                this.refresh();
            }
            return true;
        } else {
            if (this.modal) {
                this.modal.modal('hide');
                $('body').removeClass('modal-open');
                $('.modal-backdrop').remove();
                this.modal.remove();
                this.modal = null;
            }
            if (this.container) {
                this.container.remove();
                this.container = null;
            }
            return false;
        }
    }

    private refresh(): void {
        if (this.container) {
            this.container.remove();
            this.container = null;
        }

        this.container = $('<div class="container no-select"></div>').appendTo('body');
        this.pageHeaderContainer = $('<div>').addClass('clearfix page-inner-header').appendTo(this.container);
        $('<h1 class="midi_manageUsersPage_title">Manage Outage Notification Thresholds</h1>').appendTo(this.pageHeaderContainer);
        this.loadingContainer = $('<div class="alert alert-info"><i class="fa fa-lg fa-fw fa-refresh fa-spin"></i> Loading...</div>').appendTo(this.container);

        getTreeData((err: RESTError, data: TreeDataResponse) => {
            if (err) {
                this.loadingContainer.removeClass('alert-info').addClass('alert-danger').html('<i class="fa fa-lg fa-fw fa-warning"></i> Unable to get list of data from server: ' + MainSite.escapeString(err.Message));
            } else {
                this.loadingContainer.removeClass('alert-danger').addClass('alert-info').html('<i class="fa fa-lg fa-fw fa-refresh fa-spin"></i> Loading...');
            }

            const valid: boolean = this.checkReceivedDataIntegrity(data);
            if (!valid) {
                this.loadingContainer.removeClass('alert-info').addClass('alert-danger').html('<i class="fa fa-lg fa-fw fa-warning"></i> The data received from the server does not seem valid. As a security measure, the page will not load the data received.');
            } else {
                this.data = data;

                if (this.mainSite.liveDataManager.latestData == null) {
                    this.mainSite.liveDataManager.addListener(this);
                } else {
                    this.hasLiveData = true;
                    this.render();
                }
            }
        });
    }

    private checkReceivedDataIntegrity(data: TreeDataResponse): boolean {
        let result = true;
        if (data && data.TreeNodes && $.isArray(data.TreeNodes)) {
            for (let i = 0; i < data.TreeNodes.length; i++) {
                result = this.checkNodeDataKeysValidity(data.TreeNodes[i]);

                if (!result) {
                    return false;
                }
            }
        }

        return result;
    }

    private checkNodeDataKeysValidity(node: TreeDataNode): boolean {
        let result: boolean = Object.keys(node).every(x => TreeDataNodeValidKeys.indexOf(x) >= 0); //: inspired from https://stackoverflow.com/a/44240254/753113

        if (result && node.children && $.isArray(node.children)) {
            for (let i = 0; i < node.children.length; i++) {
                result = this.checkNodeDataKeysValidity(node.children[i]);

                if (!result) {
                    return false;
                }
            }
        }

        return result;
    }

    public liveDataChanged(latestData: LiveDataSet): void {
        if (!this.hasLiveData && latestData !== null) {
            this.hasLiveData = true;
            this.render();
        }
    }

    private render(): void {
        if (this.mainSite.currentUser.Permissions.GlobalAdministrator) {
            $('<p class="midi_manageUsersPage_topParagraph">As a <strong>Global Administrator</strong>, you can  manage Outage Notifications Thresholds across all regions.</p>').appendTo(this.container);
        } else {
            var region: IRegion = this.mainSite.staticDataManager.Schema.RegionSet.Regions[this.mainSite.currentUser.Region];
            $('<p class="midi_manageUsersPage_topParagraph">As a <strong>Regional Administrator</strong> for the <strong>' + MainSite.escapeString(region.Label) + '</strong> region, you can manage Outage Notifications Thresholds for sites within your region.</p>').appendTo(this.container);
        }

        $('<p class="midi_manageUsersPage_topParagraph">Thresholds are hierarchical. Where a threshold is set to a Parameter, it will be used. If a Parameter does not have a threshold set, but either its Sensor <strong>or</strong> Station have a threshold set, then the latter will be used for that Parameter (it is <em>inherited</em> from the closest parent). If a Parameter does not have a threshold set, and nor does its Sensor and Station, then the default threshold will be used.</p>').appendTo(this.container);
        $('<p class="midi_manageUsersPage_topParagraph" style="margin-bottom:30px;">Click on a row or on the edit button to modify a threshold. Thresholds are indicated like so:<br>- <em class="text-muted op-fade op-hover-100 op-40">60&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</em> The <strong>default</strong> threshold to be used when none set or inherited,<br>- <strong class="text-primary">120&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</strong> The threshold is <strong>set</strong> against this item (either Parameter, Sensor or Station, but not by way of inheritance from a parent item),<br>- <em class="text-primary"><i class="fa fa-fw fa-mail-reply-all fa-rotate-180 op-40"></i> 90&nbsp;&nbsp;</em> The threshold is <strong>inherited</strong> from a parent item (either the Sensor if it has a threshold set, or the Station).</p>').appendTo(this.container);

        let $innerNavWrap: JQuery = $('<div>').appendTo(this.container);
        let $innerNavWrapRow: JQuery = $('<div>').addClass('row').appendTo($innerNavWrap);

        let $innerNav: JQuery = $('<div>').addClass('btn-group btn-group-sm').appendTo($('<div>').addClass('col-sm-3').appendTo($innerNavWrapRow));
        let $innerFilterWrap: JQuery = $('<div>').addClass('input-group input-group-sm').appendTo($('<div>').addClass('col-sm-3').appendTo($innerNavWrapRow));
        let $innerExtrasWrap: JQuery = $('<div>').addClass('col-sm-6').appendTo($innerNavWrapRow);


        let $table: JQuery = $('<table>').addClass('table table-hover midi_common_table').appendTo(this.container);
        $table.append('<colgroup><col width="*"></col><col width="90px"></col><col width="50px"></col></colgroup>');
        $table.append('<thead><tr><th></th><th class="text-center">Threshold <span class="text-muted op-60">(Minutes)</span></th><th></th></tr></thead>');
        $table.append('<tbody></tbody>');

        let treeOpts: Fancytree.FancytreeOptions = {
            extensions: ["table", "filter"],
            checkbox: false,
            table: {
                indentation: 20,      // indent 20px per node level
                nodeColumnIdx: 0,     // render the node title into the 2nd column
                checkboxColumnIdx: -1  // render the checkboxes into the 1st column
            },
            filter: {
                autoApply: false,
                autoExpand: true,
                counter: false,
                fuzzy: true,
                hideExpanders: true,
                highlight: true,
                mode: 'hide'
            },
            source: this.data.TreeNodes,
            clickFolderMode: 4,
            click: (event, data) => {
                if (data.targetType == "expander") { //: https://wwwendt.de/tech/fancytree/doc/jsdoc/global.html#FancytreeEvents
                    console.info("NODE CLICK - EXPANDER (IGNORED)", { event: event, data: data });
                    return true; //: otherwise expansion of folders may be prevents further down.
                } else {
                    if (this.checkboxEnableRowClick.is(":checked")) {
                        this.renderModalEdit(data.node);
                    } else {
                        return true;
                    }
                }
                return false; //: NB: this prevents expansion of folders.
            },
            renderColumns: (event, data) => {
                let node = data.node, $tdList = $(node.tr).find(">td");
                let tnd: TreeDataNode = data.node.data as TreeDataNode;

                $tdList.eq(1).addClass("text-right");
                $tdList.eq(2).addClass("text-center");

                if (tnd.NodeType !== "Region") {
                    $tdList.eq(1).html(this.treeGenerateMinutesCellHTMLString(tnd));

                    let $edit: JQuery = $('<button>').appendTo($tdList.eq(2)).addClass('btn btn-xs btn-link').html('<i class="fa fa-fw fa-lg fa-pencil-square-o"></i> <span class="sr-only">Edit</span>').on("click", (e) => {
                        e.stopPropagation();
                        $(e.target).blur();
                        this.renderModalEdit(node);
                    });
                }
            },
            renderTitle: (event, data) => {
                let tnd: TreeDataNode = data.node.data as TreeDataNode;
                if (tnd.NodeType == "Region") {
                    return `<span class="fancytree-title"><i class="fa fa-fw fa-lg ${MainSite.getAwesomeIconClass('region')}"></i> <strong>${MainSite.escapeString(data.node.title)}</strong></span>`;
                } else if (tnd.NodeType == "Station") {
                    return `<span class="fancytree-title" title="${MainSite.escapeString(tnd.RegionLabel)}"><i class="fa fa-fw fa-lg  ${MainSite.getAwesomeIconClass('station')}"></i> ${MainSite.escapeString(data.node.title)}</span>`;
                } else if (tnd.NodeType == "Sensor") {
                    return `<span class="fancytree-title" title="${MainSite.escapeString(tnd.RegionLabel)} / ${MainSite.escapeString(tnd.StationLabel)}"><i class="fa fa-fw  ${MainSite.getAwesomeIconClass('sensor')}"></i> ${MainSite.escapeString(data.node.title)}</span>`;
                } else {
                    return `<span class="fancytree-title" title="${MainSite.escapeString(tnd.RegionLabel)} / ${MainSite.escapeString(tnd.StationLabel)} / ${MainSite.escapeString(tnd.SensorLabel)}"><i class="fa fa-fw  ${MainSite.getAwesomeIconClass('parameter')}"></i> ${MainSite.escapeString(data.node.title)}</span>`;
                }
            }
        };

        $table.fancytree(treeOpts);
        this.$treeCont = $table;
        this.tree = $table.fancytree('getTree');

        //: add buttons to nav, input/buttons to filter:
        $('<button>').appendTo($innerNav).addClass('btn btn-default').html('<i class="fa fa-fw fa-minus-square"></i> <span>Collapse All</span>').on("click", (e) => {
            this.treeExpand(false);
            $(e.target).blur();
        });
        $('<button>').appendTo($innerNav).addClass('btn btn-default').html('<i class="fa fa-fw fa-plus-square"></i> <span>Expand All</span>').on("click", (e) => {
            this.treeExpand(true);
            $(e.target).blur();
        });

        let $checkboxWrap: JQuery = $('<label>').addClass('checkbox-inline').text('Show only items with thresholds set').appendTo($innerExtrasWrap);
        this.filterItemsSetCheckbox = $('<input type="checkbox">').prependTo($checkboxWrap).on("change", (e) => {
            this.treeApplyFilters();
        });

        $checkboxWrap = $('<label class="checkbox-inline" data-toggle="tooltip" title="Untick this box to disable the editor upon clicking on a row (use the edit button only).">Clicking a row opens the editor</label>').appendTo($innerExtrasWrap);
        this.checkboxEnableRowClick = $('<input type="checkbox" checked>').prependTo($checkboxWrap);

        $('<span class="input-group-addon"><i class="fa fa-fw fa-filter"></i></span>').appendTo($innerFilterWrap);
        this.filterInput = $('<input type="search" placeholder="Filter..." />').addClass('form-control').appendTo($innerFilterWrap);
        $('<button>').appendTo($('<span class="input-group-btn"></span>').appendTo($innerFilterWrap)).addClass('btn btn-default').html('<i class="fa fa-fw fa-times"></i> <span class="sr-only">Clear filter</span>').on("click", (e) => {
            $(e.target).blur();
            this.filterInput.val('');
            // this.tree.clearFilter();
            this.treeApplyFilters();
        });

        this.filterInput.on("keyup", (e) => {
            this.treeApplyFilters();
        });


        $('[data-toggle="tooltip"]').tooltip();
        this.loadingContainer.hide();
    }

    private treeApplyFilters(): void {
        const onlyWithSetThreshold: boolean = this.filterItemsSetCheckbox.is(":checked");

        try { //: note try/catch primarily added to prevent page failures, for a non-essential feature. Also: using .replace() instead of .trim() as the latter may not be supported in older browsers. The .replace() regex was taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/Trim
            if ($(this.filterInput).val().replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '') == "") {
                // this.tree.clearFilter();
                // return;
            }

            let val = MainSite.escapeString($(this.filterInput).val()).toUpperCase();
            this.tree.filterNodes((node: Fancytree.FancytreeNode) => {  // this.tree.filterNodes($(this.filterInput).val());
                let tnd: TreeDataNode = node.data as TreeDataNode;
                if (val == ":SET") { //: special filter for items where there is a record set against.
                    if (tnd.AutoNotificationThreshold == null) {
                        return false;
                    } else {
                        return true;
                    }
                }

                if (
                    (
                        node.title.toUpperCase().indexOf(val) >= 0
                        || tnd.RegionLabel.toUpperCase().indexOf(val) >= 0
                        || (tnd.StationLabel && tnd.StationLabel.toUpperCase().indexOf(val) >= 0)
                        || (tnd.SensorLabel && tnd.SensorLabel.toUpperCase().indexOf(val) >= 0)
                        || (tnd.ParameterLabel && tnd.ParameterLabel.toUpperCase().indexOf(val) >= 0)
                    )
                    &&
                    (!onlyWithSetThreshold || (tnd.AutoNotificationThreshold !== null))
                ) {
                    return true;
                }
                return false;
            });
        } catch (err) {
            console.warn("Tree filter error: ", err);
        }
    }

    private treeExpand(expand: boolean = true): void { //: https://wwwendt.de/tech/fancytree/doc/jsdoc/jquery.fancytree.js.html#line3137 (method added in v2.30, we've got 2.9 at time of writing, with too many breaking changes if upgrading now.)
        this.mainSite.pageBlock();

        expand = expand !== false;
        this.tree.visit((node) => {
            if (node.hasChildren() && node.isExpanded() !== expand) {
                node.setExpanded(expand);
            }
        });

        this.mainSite.pageUnblock();
    }

    private treeGenerateMinutesCellHTMLString(tnd: TreeDataNode): string {
        let result = "";
        if (tnd.NodeType !== "Region") {
            if (tnd.TreeNodeThresholdInheritFrom == "Default") {
                result = `<em class="text-muted op-fade op-hover-100 op-40" title="None set (uses default threshold)" data-toggle="tooltip">${MainSite.escapeString(tnd.TreeNodeThresholdMinutes.toString())}</em>`;
            } else {
                if (tnd.NodeType == tnd.TreeNodeThresholdInheritFrom && tnd.AutoNotificationThreshold !== null) {
                    result = `<strong class="text-primary" title="Set on this ${MainSite.escapeString(tnd.NodeType)}" data-toggle="tooltip">${MainSite.escapeString(tnd.AutoNotificationThreshold.MinutesThreshold.toString())}</strong>`;
                } else {
                    result = `<em class="text-primary" title="Inherited from ${MainSite.escapeString(tnd.TreeNodeThresholdInheritFrom)}" data-toggle="tooltip"><i class="fa fa-fw fa-mail-reply-all fa-rotate-180 op-40"></i> ${MainSite.escapeString(tnd.TreeNodeThresholdMinutes.toString())}</em>`;
                }
            }
        }

        return result;
    }

    private finishEditing(): void {
        $('[data-toggle="tooltip"]:not([data-original-title])').tooltip(); //: helps by re-applying tooltips on edited cells/contents.
        this.isEditingEntry = false;
        this.mainSite.pageUnblock();
    }

    private updateTreeNodesAfterEdit(node: Fancytree.FancytreeNode, editResponse: EditResponse): void {
        let tnd: TreeDataNode = node.data as TreeDataNode;

        tnd.AutoNotificationThreshold = editResponse.AutoNotificationThreshold;

        if (editResponse.AutoNotificationThreshold == null) { //: threshold has been deleted.
            let parentWithThreshold: TreeDataNode = this.checkParentNodes(node, editResponse);
            if (parentWithThreshold !== null && parentWithThreshold.AutoNotificationThreshold) {
                tnd.TreeNodeThresholdMinutes = parentWithThreshold.AutoNotificationThreshold.MinutesThreshold;
                tnd.TreeNodeThresholdInheritFrom = parentWithThreshold.NodeType;
            } else {
                tnd.TreeNodeThresholdMinutes = editResponse.AutoNotificationThreshold !== null ? editResponse.AutoNotificationThreshold.MinutesThreshold : this.data.DefaultMinutesThreshold;
                tnd.TreeNodeThresholdInheritFrom = editResponse.AutoNotificationThreshold !== null ? tnd.NodeType : "Default";
            }
        } else { //: threshold has been added/edited.
            tnd.TreeNodeThresholdMinutes = editResponse.AutoNotificationThreshold.MinutesThreshold;
            tnd.TreeNodeThresholdInheritFrom = tnd.NodeType;
        }

        $(node.tr).find(">td").eq(1).html(this.treeGenerateMinutesCellHTMLString(tnd));

        //: update any children:
        if (node.hasChildren()) {
            this.updateTreeChildrenNodes(node.children, node, editResponse);
        } else {
            this.finishEditing();
        }
    }

    private checkParentNodes(node: Fancytree.FancytreeNode, editResponse: EditResponse): TreeDataNode {
        let parentWithThresholdMatched: TreeDataNode = null;

        node.visitParents(parent => {
            let parentTnd: TreeDataNode = parent.data as TreeDataNode;
            if (parentTnd.AutoNotificationThreshold !== null) {
                parentWithThresholdMatched = parentTnd;
                return false;
            }
        });

        return parentWithThresholdMatched;
    }

    private updateTreeChildrenNodes(children: Fancytree.FancytreeNode[], parent: Fancytree.FancytreeNode, editResponse: EditResponse): void {
        let parentTnd: TreeDataNode = parent.data as TreeDataNode;

        children.forEach((node, i) => {
            let tnd: TreeDataNode = node.data as TreeDataNode;

            if (tnd.AutoNotificationThreshold !== null) { //: stop iteration if the current child has a threshold set.
                return;
            }

            if (tnd.AutoNotificationThreshold == null && editResponse.AutoNotificationThreshold !== null) { //: threshold has been added/edited.
                tnd.TreeNodeThresholdMinutes = editResponse.AutoNotificationThreshold.MinutesThreshold;
                tnd.TreeNodeThresholdInheritFrom = parentTnd.NodeType;
            }

            if (tnd.AutoNotificationThreshold == null && editResponse.AutoNotificationThreshold == null) { //: threshold has been deleted.
                let parentWithThreshold: TreeDataNode = this.checkParentNodes(node, editResponse);
                if (parentWithThreshold !== null && parentWithThreshold.AutoNotificationThreshold) {
                    tnd.TreeNodeThresholdMinutes = parentWithThreshold.AutoNotificationThreshold.MinutesThreshold;
                    tnd.TreeNodeThresholdInheritFrom = parentWithThreshold.NodeType;
                } else {
                    tnd.TreeNodeThresholdMinutes = editResponse.AutoNotificationThreshold !== null ? editResponse.AutoNotificationThreshold.MinutesThreshold : this.data.DefaultMinutesThreshold;
                    tnd.TreeNodeThresholdInheritFrom = editResponse.AutoNotificationThreshold !== null ? tnd.NodeType : "Default";
                }
            }

            $(node.tr).find(">td").eq(1).html(this.treeGenerateMinutesCellHTMLString(tnd));

            if (node.hasChildren()) {
                this.updateTreeChildrenNodes(node.children, node, editResponse);
            } else {
                this.finishEditing();
            }
        });
    }

    private renderModalEdit(node: Fancytree.FancytreeNode): void {
        $('[data-toggle="tooltip"]').tooltip('hide');

        if (this.isEditingEntry) {
            console.warn("NODE CLICK ERROR: an entry is already being (or about to be) edited. Please wait for edition to be completed before trying to edit another entry.");
            return;
        }

        const tnd: TreeDataNode = node.data as TreeDataNode;
        if (tnd.NodeType == "Region") {
            console.warn("NODE CLICK IGNORED: user clicked a 'Region' node.");
            return;
        }

        this.isEditingEntry = true;
        this.mainSite.pageBlock();

        const addNew = tnd.AutoNotificationThreshold == null;
        let modalDataClone: AutoNotificationThreshold = tnd.AutoNotificationThreshold !== null ? { ...tnd.AutoNotificationThreshold } : { Id: null, StationId: tnd.StationId, SensorId: tnd.SensorId, ParameterId: tnd.ParameterId, MinutesThreshold: null }; //: use a clone or new object to help update data
        const defaultThreshold: number = this.data.DefaultMinutesThreshold;
        const appliesToLabel: string = `${MainSite.escapeString(tnd.RegionLabel)} / ${MainSite.escapeString(tnd.StationLabel)}${tnd.NodeType == "Sensor" || tnd.NodeType == "Parameter" ? ' / ' + MainSite.escapeString(tnd.SensorLabel) : ''}${tnd.NodeType == "Parameter" ? ' / ' + MainSite.escapeString(tnd.ParameterLabel) : ''}`;
        // console.info("MODAL: ", { addNew: addNew, node: node, tnd: tnd, modalDataClone: modalDataClone });

        if (this.modal) {
            this.modal.remove();
            this.modal = null;
        }

        this.modal = $('<div class="modal fade" data-backdrop="static"></div>').appendTo('body');
        let modalDialog: JQuery = $('<div class="modal-dialog"></div>').appendTo(this.modal);
        let modalContent: JQuery = $('<div class="modal-content"></div>').appendTo(modalDialog);

        //: Modal Header:
        let modalHeaderContent: JQuery = $('<div class="modal-header"></div>').appendTo(modalContent);
        $('<button type="button" class="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>').appendTo(modalHeaderContent);
        if (addNew) {
            $('<h4 class="modal-title">Set Outage Notification Threshold</h4>').appendTo(modalHeaderContent);
        } else {
            $('<h4 class="modal-title">Edit Outage Notification Threshold</h4>').appendTo(modalHeaderContent);
        }

        //: Modal Content:
        this.modalBodyContent = $('<div class="modal-body"></div>').appendTo(modalContent);
        let modalFeedbackWrap: JQuery = $('<div class="modal-feedback"></div>').appendTo(this.modalBodyContent);

        //: Form wrapper:
        let modalForm: JQuery = $('<form></form>').appendTo(this.modalBodyContent);
        modalForm.addClass("form-horizontal");
        modalForm.on("submit", (e) => {
            return false;
        });

        //: reusable Jquery placeholders + input elements:
        let $row: JQuery,
            $label: JQuery,
            $field: JQuery;

        let $inputMinutesThreshold: JQuery;

        //: Field: Station/Sensor/Param (info, non-editable):
        $row = $('<div class="form-group"></div>').appendTo(modalForm);
        $label = $('<label class="col-sm-3 control-label">Applies to:</label>').appendTo($row);
        $field = $('<div class="col-sm-9"></div>').appendTo($row);
        $(`<p class="form-control-static">${MainSite.escapeString(appliesToLabel)}</p>`).appendTo($field);

        //: Field: level of item (info, non-editable):
        $row = $('<div class="form-group"></div>').appendTo(modalForm);
        $label = $('<label class="col-sm-3 control-label">Level:</label>').appendTo($row);
        $field = $('<div class="col-sm-9"></div>').appendTo($row);
        $(`<p class="form-control-static">${MainSite.escapeString(tnd.NodeType)}</p>`).appendTo($field);

        //: Field: threshold inheritance (info, non-editable):
        $row = $('<div class="form-group"></div>').appendTo(modalForm);
        $label = $('<label class="col-sm-3 control-label">Inheritance:</label>').appendTo($row);
        $field = $('<div class="col-sm-9"></div>').appendTo($row);
        if (tnd.AutoNotificationThreshold !== null && tnd.TreeNodeThresholdInheritFrom == tnd.NodeType) {
            $(`<p class="form-control-static"><em class="text-muted">none (threshold set for this ${MainSite.escapeString(tnd.NodeType)})</em></p>`).appendTo($field);
        } else {
            $(`<p class="form-control-static"><span class="text-primary">${MainSite.escapeString(tnd.TreeNodeThresholdMinutes.toString())}</span> minutes from <em class="text-primary">${MainSite.escapeString(tnd.TreeNodeThresholdInheritFrom)}</em></p>`).appendTo($field);
        }


        //: Field: minutes threshold value:
        $row = $('<div class="form-group"></div>').appendTo(modalForm);
        $label = $('<label class="col-sm-3 control-label">Threshold:</label>').appendTo($row);
        $field = $('<div class="col-sm-4"></div>').appendTo($row); //: NB: not using full-width here..
        let $ig: JQuery = $('<div>').addClass('input-group input-group-sm').appendTo($field);
        $inputMinutesThreshold = $(`<input class="form-control" type="number" min="0" placeholder="${MainSite.escapeString(defaultThreshold.toString())}" value="${tnd.AutoNotificationThreshold == null ? '' : MainSite.escapeString(tnd.AutoNotificationThreshold.MinutesThreshold.toString())}" />`).appendTo($ig);
        $('<span>').addClass('input-group-addon').text('Minutes').appendTo($ig);


        //: additional event handlers:
        modalForm.find(':input').on('change', (e) => {
            modalForm.find('.has-error, .has-warning, .midi_common_validation_warning').removeClass('has-error has-warning midi_common_validation_warning');
            modalFeedbackWrap.html('').hide();
            modalForm.addClass('data-changed');
            window.onbeforeunload = () => {
                return "You have unsaved modifications on this page. If you navigate away from the page, your modifications will not be saved.";
            };
        });


        //: Footer:
        let modalFooterContent: JQuery = $('<div class="modal-footer"></div>').appendTo(modalContent);
        this.modalSaveButton = $('<button type="button" class="btn btn-success">Save</button>')
            .appendTo(modalFooterContent)
            .click(() => {
                // console.info("save...", { old: currentSetting, new: modalDataClone });
                modalForm.find('.has-error, .has-warning, .midi_common_validation_warning').removeClass('has-error has-warning midi_common_validation_warning');
                modalFeedbackWrap.html('').hide();

                let errors: string[] = [];

                if (isNaN(parseInt($inputMinutesThreshold.val())) || $inputMinutesThreshold.val() == '') {
                    errors.push("Please set a valid number for the minutes threshold to use.");
                }

                if (errors.length > 0) {
                    modalFeedbackWrap.html('<div class="alert alert-danger"><i class="fa fa-lg fa-warning"></i> <strong>Please correct the below errors:</strong><br><br>- ' + errors.join('<br>- ') + '</div>').show();
                    return false;
                }

                //: ensure we have values from the modal form:
                modalDataClone.MinutesThreshold = parseInt($inputMinutesThreshold.val());

                if (errors.length == 0) {
                    const req: SetRequest = modalDataClone;
                    this.mainSite.pageBlock();
                    setSingle(req, (err: RESTError, data: EditResponse, xhr: JQueryXHR) => {
                        if (err) {
                            errors.push("Server error: " + MainSite.escapeString(err.Message));
                            modalFeedbackWrap.html('<div class="alert alert-danger"><i class="fa fa-lg fa-warning"></i> <strong>Unable to save your changes:</strong><br><br>- ' + errors.join('<br>- ') + '</div>').show();
                            this.mainSite.pageUnblock();
                            return false;
                        }
                        modalForm.removeClass('data-changed');
                        if (xhr.status !== 304) { //: 304 NotModified (empty response! - see https://docs.microsoft.com/en-us/dotnet/api/system.net.httpstatuscode?view=net-5.0)
                            this.updateTreeNodesAfterEdit(node, data); //: NB: this calls this.finishEditing() to return UI to usable state.
                        }
                        this.modal.modal('hide');
                    });
                }
            });

        $('<button type="button" class="btn btn-default" data-dismiss="modal">Close</button>').appendTo(modalFooterContent);

        if (!addNew) {
            this.modalDeleteButton = $('<button type="button" class="btn btn-danger pull-left">Reset to default</button>')
                .prependTo(modalFooterContent)
                .click(() => {
                    modalForm.find('.has-error, .has-warning, .midi_common_validation_warning').removeClass('has-error has-warning midi_common_validation_warning');
                    modalFeedbackWrap.html('').hide();

                    let errors: string[] = [];


                    if (errors.length > 0) {
                        modalFeedbackWrap.html('<div class="alert alert-danger"><i class="fa fa-lg fa-warning"></i> <strong>Please correct the below errors:</strong><br><br>- ' + errors.join('<br>- ') + '</div>').show();
                        return false;
                    }

                    if (errors.length == 0) {
                        let conf = confirm(`Are you certain you wish to permanently delete the settings for ${MainSite.escapeString(appliesToLabel)}?`);
                        if (conf) {
                            const req: DeleteRequest = {
                                Id: tnd.AutoNotificationThreshold.Id,
                                StationId: tnd.AutoNotificationThreshold.StationId
                            };

                            deleteSingle(req, (err: RESTError, data: EditResponse) => {
                                if (err) {
                                    errors.push("Server error: " + MainSite.escapeString(err.Message));
                                    modalFeedbackWrap.html('<div class="alert alert-danger"><i class="fa fa-lg fa-warning"></i> <strong>Unable to save your changes:</strong><br><br>- ' + errors.join('<br>- ') + '</div>').show();
                                    this.mainSite.pageUnblock();
                                    return false;
                                }
                                modalForm.removeClass('data-changed');
                                this.updateTreeNodesAfterEdit(node, data); //: NB: this calls this.finishEditing() to return UI to usable state.
                                this.modal.modal('hide');
                            });
                        }
                    }
                });
        }

        this.modal.on('shown.bs.modal', (e) => {
            this.mainSite.pageUnblock();
        });

        this.modal.on('hide.bs.modal', (e) => {
            if (modalForm.hasClass('data-changed')) {
                let conf = confirm("You have unsaved changes on this popup.\n\nAre you sure you wish to cancel and close the popup?");
                if (!conf) {
                    return false;
                }
            }
        });

        this.modal.on('hidden.bs.modal', (e) => {
            this.modal.remove();
            this.modal = null;
            this.isEditingEntry = false;
            this.mainSite.pageUnblock();
            window.onbeforeunload = null;
        });

        this.modal.modal('show');
    }
}